import { useState } from 'react';
import { Pencil, Check, X, History } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Inline editor for any target across ScaleBoard.
 *
 * Props:
 *   value          — current target in rupees (number).
 *   entityType     — 'division' | 'outlet' | 'group'.
 *   entityId       — division id, outlet id, or 'global' for group.
 *   label          — short label for display (e.g. "LC", "Xiaomi", "Group").
 *   onSaved        — callback fired after a successful save (parent should re-fetch).
 *   compact        — when true, renders just a tiny pencil next to the value;
 *                    when false (default), renders the value + pencil aligned.
 *   readOnly       — disable editing entirely (e.g. when user isn't CEO).
 *   formatValue    — function (number) → string for display. Defaults to ₹X.XXCr.
 *   testId         — data-testid for the editable wrapper.
 *
 * Visible only to role === 'ceo' unless readOnly is forced.
 */
export default function EditableTarget({
  value,
  entityType,
  entityId,
  label,
  onSaved,
  compact = false,
  readOnly = false,
  formatValue,
  testId,
}) {
  const { user } = useAuth();
  const isCEO = user?.role === 'ceo';
  const canEdit = isCEO && !readOnly;

  const [open, setOpen] = useState(false);
  const [draftCr, setDraftCr] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);

  const fmt = formatValue || ((v) => {
    if (!v) return '₹0';
    if (v >= 10000000) return `₹${(v / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
    if (v >= 100000) return `₹${(v / 100000).toFixed(2).replace(/\.?0+$/, '')}L`;
    return `₹${Number(v).toLocaleString('en-IN')}`;
  });

  const startEdit = () => {
    setDraftCr(value ? (value / 10000000).toFixed(2) : '');
    setReason('');
    setError('');
    setOpen(true);
  };

  const cancel = () => {
    setOpen(false);
    setDraftCr('');
    setReason('');
    setError('');
  };

  const save = async () => {
    setError('');
    const cr = parseFloat(draftCr);
    if (isNaN(cr) || cr < 0) {
      setError('Enter a valid number ≥ 0');
      return;
    }
    setSaving(true);
    try {
      const newRupees = Math.round(cr * 10000000);
      const path =
        entityType === 'group'
          ? '/admin/targets/group'
          : entityType === 'outlet'
          ? `/admin/targets/outlet/${entityId}`
          : `/admin/targets/division/${entityId}`;
      await api.patch(path, { value: newRupees, reason });
      setOpen(false);
      setDraftCr('');
      setReason('');
      if (onSaved) onSaved({ newValue: newRupees, oldValue: value });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await api.get(`/admin/targets/audit-log`, {
        params: { entity_id: entityId, limit: 20 },
      });
      setHistory(r.data?.items || []);
      setHistoryOpen(true);
    } catch {
      setHistory([]);
      setHistoryOpen(true);
    }
  };

  if (!canEdit) {
    return <span data-testid={testId}>{fmt(value)}</span>;
  }

  return (
    <span className="relative inline-flex items-center gap-1" data-testid={testId}>
      {!open && <span>{fmt(value)}</span>}
      {!open && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            startEdit();
          }}
          className="opacity-50 hover:opacity-100 transition-opacity"
          title={`Edit ${label || 'target'}`}
          data-testid={`edit-target-${entityType}-${entityId}`}
        >
          <Pencil className="h-3 w-3" style={{ color: '#3B5BA9' }} />
        </button>
      )}

      {open && (
        <span
          onClick={(e) => e.stopPropagation()}
          className="inline-flex flex-col gap-1 bg-white border rounded-md p-2 shadow-md"
          style={{ borderColor: '#3B5BA9', minWidth: 220 }}
        >
          <span className="block text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#1B2A4A' }}>
            Edit {label || 'target'} (in ₹Cr)
          </span>
          <span className="flex items-center gap-1">
            <span className="text-xs" style={{ color: '#6B7280' }}>₹</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={draftCr}
              onChange={(e) => setDraftCr(e.target.value)}
              className="border rounded px-2 py-1 text-sm flex-1"
              style={{ borderColor: '#E5E1DC', maxWidth: 90 }}
              autoFocus
              data-testid="edit-target-input-cr"
            />
            <span className="text-xs" style={{ color: '#6B7280' }}>Cr</span>
          </span>
          <input
            type="text"
            placeholder="Why? (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="border rounded px-2 py-1 text-[11px]"
            style={{ borderColor: '#E5E1DC' }}
            data-testid="edit-target-reason"
          />
          {error && <span className="block text-[10px]" style={{ color: '#A32D2D' }}>{error}</span>}
          <span className="flex items-center justify-between gap-1">
            <span className="flex gap-1">
              <button
                onClick={save}
                disabled={saving}
                className="px-2 py-1 rounded text-xs inline-flex items-center gap-1 disabled:opacity-60"
                style={{ backgroundColor: '#3B5BA9', color: 'white' }}
                data-testid="edit-target-save"
              >
                <Check className="h-3 w-3" /> {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={cancel}
                className="px-2 py-1 rounded text-xs inline-flex items-center gap-1"
                style={{ backgroundColor: '#F3F4F6', color: '#1B2A4A' }}
                data-testid="edit-target-cancel"
              >
                <X className="h-3 w-3" /> Cancel
              </button>
            </span>
            <button
              onClick={loadHistory}
              className="px-2 py-1 rounded text-[11px] inline-flex items-center gap-1 hover:bg-gray-100"
              style={{ color: '#6B7280' }}
              title="View change history"
              data-testid="edit-target-history"
            >
              <History className="h-3 w-3" /> History
            </button>
          </span>

          {historyOpen && (
            <span
              className="block mt-1 max-h-44 overflow-y-auto border-t pt-1 text-[10px]"
              style={{ borderColor: '#E5E1DC', color: '#374151' }}
            >
              {history.length === 0 && (
                <span className="block text-center py-1 italic" style={{ color: '#9CA3AF' }}>
                  No changes recorded yet
                </span>
              )}
              {history.map((h, i) => (
                <span key={i} className="block py-1 border-b last:border-b-0" style={{ borderColor: '#F3F4F6' }}>
                  <span className="flex justify-between">
                    <span className="font-mono">{(h.old_value / 10000000).toFixed(2)}Cr → {(h.new_value / 10000000).toFixed(2)}Cr</span>
                    <span style={{ color: '#9CA3AF' }}>
                      {new Date(h.changed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </span>
                  </span>
                  <span className="block" style={{ color: '#6B7280' }}>{h.changed_by}</span>
                  {h.reason && <span className="block italic mt-0.5" style={{ color: '#6B7280' }}>"{h.reason}"</span>}
                </span>
              ))}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
